import {
  Client,
  Opportunity,
  OpportunityEntryKind,
  OpportunityPipelineStatus,
} from '../types';
import { OpportunityInput } from '../services/opportunitiesService';
import { commitNotes } from './opportunityNotes';
import {
  OpportunityFieldKey,
  getCumulativeRequiredFields,
  getDefaultProbability,
  getMissingRequiredFields,
  getNextPipelineStatus,
  getStagesUpToStatus,
  OPPORTUNITY_STAGE_CONFIG,
} from './commercialPlanning';
import { formatMissingFieldsMessage } from './formValidation';

export type OpportunityFormValues = {
  id?: string;
  title: string;
  status: OpportunityPipelineStatus;
  entry_kind: OpportunityEntryKind | '';
  client_id: number | '';
  contact_name: string;
  email: string;
  source: string;
  probability_to_win: string;
  estimated_value: string;
  project_type: string;
  project_identified: '' | 'yes' | 'no' | 'to_be_defined';
  customer_need: string;
  project_location: string;
  expected_decision_date: string;
  opportunity_value: string;
  expected_closing_date: string;
  proposal_amount: string;
  proposal_date: string;
  lost_reason: string;
  reason: string;
  final_value: string;
  closing_date: string;
  commercial_owner_id: number | '';
  office_id: number | '';
  next_action_date: string;
  notes: string;
  notesBaseline: string;
};

export const EMPTY_OPPORTUNITY_FORM: OpportunityFormValues = {
  title: '',
  status: 'new',
  entry_kind: 'lead',
  client_id: '',
  contact_name: '',
  email: '',
  source: '',
  probability_to_win: String(getDefaultProbability('new')),
  estimated_value: '',
  project_type: '',
  project_identified: '',
  customer_need: '',
  project_location: '',
  expected_decision_date: '',
  opportunity_value: '',
  expected_closing_date: '',
  proposal_amount: '',
  proposal_date: '',
  lost_reason: '',
  reason: '',
  final_value: '',
  closing_date: '',
  commercial_owner_id: '',
  office_id: '',
  next_action_date: '',
  notes: '',
  notesBaseline: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && EMAIL_RE.test(trimmed);
}

export function formFromLostLead(opportunity: Opportunity): OpportunityFormValues {
  return {
    ...EMPTY_OPPORTUNITY_FORM,
    client_id: opportunity.client_id || '',
    contact_name: opportunity.contact_name || '',
    email: opportunity.email || '',
    source: opportunity.source || '',
    commercial_owner_id: opportunity.commercial_owner_id || '',
    office_id: opportunity.office_id || '',
  };
}

export function opportunityToFormValues(opportunity: Opportunity): OpportunityFormValues {
  return {
    id: opportunity.id,
    title: opportunity.title,
    status: (opportunity.status as OpportunityPipelineStatus) || 'new',
    entry_kind: opportunity.entry_kind || 'lead',
    client_id: opportunity.client_id || '',
    contact_name: opportunity.contact_name || '',
    email: opportunity.email || '',
    source: opportunity.source || '',
    probability_to_win: String(opportunity.probability_to_win),
    estimated_value: opportunity.estimated_value != null ? String(opportunity.estimated_value) : '',
    project_type: opportunity.project_type || '',
    project_identified: opportunity.project_identified || '',
    customer_need: opportunity.customer_need || '',
    project_location: opportunity.project_location || '',
    expected_decision_date: opportunity.expected_decision_date || '',
    opportunity_value: opportunity.opportunity_value != null ? String(opportunity.opportunity_value) : '',
    expected_closing_date: opportunity.expected_closing_date || '',
    proposal_amount: opportunity.proposal_amount != null ? String(opportunity.proposal_amount) : '',
    proposal_date: opportunity.proposal_date || '',
    lost_reason: opportunity.lost_reason || '',
    reason: opportunity.reason || '',
    final_value: opportunity.final_value != null ? String(opportunity.final_value) : '',
    closing_date: opportunity.closing_date || '',
    commercial_owner_id: opportunity.commercial_owner_id || '',
    office_id: opportunity.office_id || '',
    next_action_date: opportunity.next_action_date || '',
    notes: opportunity.notes || '',
    notesBaseline: opportunity.notes || '',
  };
}

export type FormValuesToInputOptions = {
  notesAuthor?: string;
  now?: Date;
};

export function formValuesToInput(
  form: OpportunityFormValues,
  options?: FormValuesToInputOptions,
): OpportunityInput {
  const notes = commitNotes(
    form.notes,
    form.notesBaseline || '',
    options?.notesAuthor || 'Unknown',
    options?.now ?? new Date(),
  );
  return {
    title: form.title.trim(),
    status: form.status,
    entry_kind: (form.entry_kind || 'lead') as OpportunityEntryKind,
    client_id: form.client_id || undefined,
    contact_name: form.contact_name.trim() || undefined,
    email: form.email.trim() || undefined,
    source: form.source.trim() || undefined,
    probability_to_win: Number(form.probability_to_win || getDefaultProbability(form.status)),
    estimated_value: form.estimated_value ? Number(form.estimated_value) : undefined,
    project_type: form.project_type || undefined,
    project_identified: form.project_identified || undefined,
    customer_need: form.customer_need.trim() || undefined,
    project_location: form.project_location.trim() || undefined,
    expected_decision_date: form.expected_decision_date || undefined,
    opportunity_value: form.opportunity_value ? Number(form.opportunity_value) : undefined,
    expected_closing_date: form.expected_closing_date || undefined,
    proposal_amount: form.proposal_amount ? Number(form.proposal_amount) : undefined,
    proposal_date: form.proposal_date || undefined,
    lost_reason: form.lost_reason.trim() || undefined,
    reason: form.reason.trim() || undefined,
    final_value: form.final_value ? Number(form.final_value) : undefined,
    closing_date: form.closing_date || undefined,
    commercial_owner_id: form.commercial_owner_id || undefined,
    office_id: form.office_id || undefined,
    next_action_date: form.next_action_date || undefined,
    notes: notes || undefined,
  };
}

export function opportunityToInput(opportunity: Opportunity): OpportunityInput {
  return formValuesToInput(opportunityToFormValues(opportunity));
}

export type OpportunityFormValidationResult = {
  message: string;
  fields: string[];
} | null;

export function validateOpportunityForm(form: OpportunityFormValues): OpportunityFormValidationResult {
  const missing = getMissingRequiredFields(form.status, form as Partial<Record<OpportunityFieldKey, unknown>>);
  const emailInvalid = Boolean(form.email.trim() && !isValidEmail(form.email));

  if (missing.length === 0 && emailInvalid) {
    return { message: 'Email must be a valid email address.', fields: ['email'] };
  }

  if (missing.length > 0) {
    const labels = missing.map((field) => getFieldLabel(field));
    const fields = emailInvalid ? [...missing, 'email'] : missing;
    const message = emailInvalid
      ? `${formatMissingFieldsMessage(labels)} Email must be a valid email address.`
      : formatMissingFieldsMessage(labels);
    return { message, fields };
  }

  return null;
}

export function getVisibleStagesForStatus(status: OpportunityPipelineStatus): OpportunityPipelineStatus[] {
  return getStagesUpToStatus(status);
}

export function getNextStepTargetStatus(
  currentStatus: OpportunityPipelineStatus,
  outcome?: 'won' | 'lost' | 'other',
): OpportunityPipelineStatus | null {
  if (currentStatus === 'negotiation') {
    return outcome || null;
  }
  return getNextPipelineStatus(currentStatus);
}

export function buildNextStepForm(
  opportunity: Opportunity,
  targetStatus: OpportunityPipelineStatus,
): OpportunityFormValues {
  const base = opportunityToFormValues(opportunity);
  return {
    ...base,
    status: targetStatus,
    probability_to_win: String(getDefaultProbability(targetStatus)),
  };
}

export function getNextStepFieldKeys(
  currentStatus: OpportunityPipelineStatus,
  targetStatus: OpportunityPipelineStatus,
  form: OpportunityFormValues,
): OpportunityFieldKey[] {
  const fields = new Set<OpportunityFieldKey>();

  if (targetStatus === 'won' || targetStatus === 'lost' || targetStatus === 'other') {
    OPPORTUNITY_STAGE_CONFIG[targetStatus].fields.forEach((field) => fields.add(field));
    getMissingRequiredFields(targetStatus, form as Partial<Record<OpportunityFieldKey, unknown>>)
      .forEach((field) => fields.add(field));
    return Array.from(fields);
  }

  OPPORTUNITY_STAGE_CONFIG[targetStatus].fields.forEach((field) => fields.add(field));
  OPPORTUNITY_STAGE_CONFIG[targetStatus].requiredFields.forEach((field) => fields.add(field));

  getMissingRequiredFields(currentStatus, form as Partial<Record<OpportunityFieldKey, unknown>>)
    .forEach((field) => fields.add(field));

  return Array.from(fields);
}

export function getStagesForFieldKeys(fieldKeys: OpportunityFieldKey[]): OpportunityPipelineStatus[] {
  const stages: OpportunityPipelineStatus[] = [];
  const ordered: OpportunityPipelineStatus[] = [
    'new',
    'qualified',
    'opportunity',
    'bidding',
    'negotiation',
    'won',
    'lost',
    'other',
  ];
  ordered.forEach((stage) => {
    if (OPPORTUNITY_STAGE_CONFIG[stage].fields.some((field) => fieldKeys.includes(field))) {
      stages.push(stage);
    }
  });
  return stages;
}

export function getFieldLabel(field: OpportunityFieldKey): string {
  const labels: Record<OpportunityFieldKey, string> = {
    title: 'Title',
    client_id: 'Company',
    contact_name: 'Lead contact name',
    email: 'Lead contact email',
    source: 'Source',
    commercial_owner_id: 'Commercial Owner',
    office_id: 'Office',
    notes: 'Notes',
    project_type: 'Project type',
    project_identified: 'Project identified?',
    project_location: 'Project physical location',
    expected_decision_date: 'Expected decision date',
    estimated_value: 'Estimated value',
    opportunity_value: 'Opportunity value',
    expected_closing_date: 'Expected closing date',
    proposal_amount: 'Proposal amount',
    proposal_date: 'Proposal date',
    next_action_date: 'Next action date',
    lost_reason: 'Lost reason',
    reason: 'Reason',
    final_value: 'Final value',
    closing_date: 'Closing date',
  };
  return labels[field];
}

export function isFieldRequired(
  field: OpportunityFieldKey,
  status: OpportunityPipelineStatus,
): boolean {
  return getCumulativeRequiredFields(status).includes(field);
}

export function findExistingClientByName(clients: Client[], candidateName: string): Client | undefined {
  const raw = candidateName.trim().toLowerCase();
  if (!raw) return undefined;
  const exact = clients.find((client) => client.name.trim().toLowerCase() === raw);
  if (exact) return exact;

  const normalize = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .join(' ');

  const normalizedCandidate = normalize(candidateName);
  if (!normalizedCandidate) return undefined;
  return clients.find((client) => normalize(client.name) === normalizedCandidate);
}
